import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import { getInitials } from '@core/util/business-user';
import { ICON } from '@core/util/icon';
import { getFullName } from '@core/util/string';
import { BusinessUser } from '@models/businesses';
import { Select } from '@ngxs/store';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { TestHooks } from './lawyer-clerk-initials.hooks';

interface LabelPair {
	name: string;
	initials: string;
}

@Component({
	selector: 'app-lawyer-clerk-initials',
	templateUrl: './lawyer-clerk-initials.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LawyerClerkInitialsComponent implements OnInit {
	testHooks = TestHooks;
	private clerkId$ = new BehaviorSubject<string>('');
	private lawyerId$ = new BehaviorSubject<string>('');

	@Select(BusinessUsersState.getUsers) users$!: Observable<BusinessUser[]>;
	clerk$!: Observable<LabelPair>;
	lawyer$!: Observable<LabelPair>;
	showPipe$!: Observable<boolean>;

	@Input()
	set clerkId(clerkId: string) {
		this.clerkId$.next(clerkId);
	}

	@Input()
	set lawyerId(lawyerId: string) {
		this.lawyerId$.next(lawyerId);
	}

	public ICON = ICON;

	ngOnInit(): void {
		this.lawyer$ = combineLatest([
			this.lawyerId$.pipe(distinctUntilChanged()),
			this.users$,
		]).pipe(
			map(([id, users]) => users.find((user) => user.id === id)),
			map((person) =>
				person
					? {
							name: getFullName([person.firstName, person.lastName]),
							initials: getInitials(person),
						}
					: null,
			),
		);

		this.clerk$ = combineLatest([
			this.clerkId$.pipe(distinctUntilChanged()),
			this.users$,
		]).pipe(
			map(([id, users]) => users.find((user) => user.id === id)),
			map((person) =>
				person
					? {
							name: getFullName([person.firstName, person.lastName]),
							initials: getInitials(person),
						}
					: null,
			),
		);

		// only show the pipe if the lawyer AND the clerk are both
		// present with values
		this.showPipe$ = combineLatest([this.lawyer$, this.clerk$]).pipe(
			map(([lawyer, clerk]) => !!(lawyer?.initials && clerk?.initials)),
		);
	}
}
